import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import CreatableSelect from "react-select/creatable";
import {
  stayMaster,
  hotelMaster,
  hotelDestinationMaster,
  stayData,
  addPackageStay,
  deletePackageStay,
  updatePackageStay,
  deleteHotelFromStay,
} from "../packageService";
import FilePreview from "../../../FilePreview/FilePreview";
import Toster from "../../../common/Toster";
import ImageInput from "../../../common/ImageInput";
import {
  pbAddBtnFull,
  pbAddBtnMt,
  pbDeleteBtn,
  pbDeleteBtnDisabled,
  pbFieldLabel,
  pbFieldInput,
  pbHotelCard,
  pbHotelCountBadge,
  pbHotelPanel,
  pbIndexBadgeSm,
  pbInnerSection,
  pbSectionCard,
  pbSectionSubtitle,
  pbSectionTitle,
  pbStayIndexBadge,
} from "../packageBuilderUi";

const StayInfo = forwardRef(({ setStayData, packageid, onUserEdit }, ref) => {
  const queryClient = useQueryClient();
  const [showToster, setShowToster] = useState(false);

  const [stayStates, setStayStates] = useState({});
  const [errors, setErrors] = useState({});
  const [hotelMasters, setHotelMasters] = useState({});
  const [incrementalStays, setIncrementalStays] = useState({});
  const loadStayDataIdRef = useRef(0);

  const notifyUserEdit = () => onUserEdit?.();

  // Fetch data using React Query
  const { data: stayMasters } = useQuery({
    queryKey: ["stayMasters"],
    queryFn: stayMaster,
  });

  const { data: oldHotelDestinations } = useQuery({
    queryKey: ["hotelDestinations", packageid],
    queryFn: () => hotelDestinationMaster(packageid),
    enabled: !!packageid,
  });

  const { data: packageStayData } = useQuery({
    queryKey: ["packageStay", packageid],
    queryFn: () => stayData(packageid),
    enabled: !!packageid,
  });

  // update stay data
  const updateMutation = useMutation({
    mutationFn: ({ formData, stayId }) => updatePackageStay(formData, stayId),
    onSuccess: () => {
      queryClient.invalidateQueries(["packageStay", packageid]);
    },
  });

  // Mutation for saving stay data
  const saveMutation = useMutation({
    mutationFn: (data) => addPackageStay(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["packageStay", packageid]);
    },
  });

  // Add delete mutation
  const deleteMutation = useMutation({
    mutationFn: deletePackageStay,
  });

  // Add delete hotel mutation
  const deleteHotelMutation = useMutation({
    mutationFn: deleteHotelFromStay,
    onSuccess: () => {
      queryClient.invalidateQueries(["packageStay", packageid]);
    },
  });

  // Initialize stay states when stay masters are loaded
  useEffect(() => {
    if (stayMasters?.data) {
      // Initialize all stays as disabled first
      const initialStates = stayMasters.data.reduce((acc, stay) => {
        acc[stay.id] = {
          enabled: false,
          hotels: [],
        };
        return acc;
      }, {});
      setStayStates(initialStates);

      // Initialize empty incremental stays
      const initialIncrementalStays = stayMasters.data.reduce((acc, stay) => {
        acc[stay.id] = [];
        return acc;
      }, {});
      setIncrementalStays(initialIncrementalStays);
    }
  }, [stayMasters]);

  // Load existing stay data
  useEffect(() => {
    const loadId = ++loadStayDataIdRef.current;

    const loadStayData = async () => {
      if (packageStayData?.data && stayMasters?.data) {
        const stayData = packageStayData.data;

        // Initialize fresh states instead of spreading existing ones
        const updatedStates = {};
        const updatedIncrementalStays = {};

        // First, reset all stays to disabled
        stayMasters.data.forEach((stay) => {
          updatedStates[stay.id] = {
            enabled: false,
            hotels: [],
          };
          updatedIncrementalStays[stay.id] = [];
        });

        // Then process existing stay data
        for (const stay of stayData) {
          const categoryId = stay.stay_category_id;

          // Enable the stay category that has data
          updatedStates[categoryId] = {
            enabled: true,
            hotels: [],
          };

          try {
            // Fetch hotel masters for the destination
            const hotelMasterResponse = await hotelMaster(
              stay.destination,
              packageid,
            );
            const availableHotels = hotelMasterResponse.data || [];

            // Get the correct index for this stay in the array
            if (!updatedIncrementalStays[categoryId]) {
              updatedIncrementalStays[categoryId] = [];
            }
            const stayIndexInArray = updatedIncrementalStays[categoryId].length;

            // Store hotel masters for this category and stay with correct index
            setHotelMasters((prev) => ({
              ...prev,
              [`${categoryId}_${stayIndexInArray}`]: availableHotels,
            }));

            // Process hotels with details
            const hotelsWithDetails = await Promise.all(
              stay.package_stay_hotels.map(async (hotelData) => {
                const hotelDetails = availableHotels.find(
                  (h) => h.id === hotelData.hotel_id,
                );
                return {
                  id: hotelData.hotel_id,
                  package_stay_hotel_id: hotelData.id, // This is the ID we need for deletion
                  type: "existing",
                  hotel_name: hotelDetails?.hotel_name || "",
                  location: hotelDetails?.location || "",
                  website: hotelDetails?.website || "",
                  image_url: hotelDetails?.image_url || null,
                };
              }),
            );

            // Update incremental stays with the stay data
            updatedIncrementalStays[categoryId].push({
              id: stay.id,
              fromServer: true,
              destination: {
                value: stay.destination,
                label: stay.destination,
                type: "existing",
              },
              duration: stay.duration,
              hotels: hotelsWithDetails,
            });
          } catch (error) {
            console.error("Error loading stay data:", error);
            setErrors({ general: ["Failed to load some stay information"] });
          }
        }

        if (loadId !== loadStayDataIdRef.current) {
          return;
        }

        // Update state
        setStayStates(updatedStates);
        setIncrementalStays(updatedIncrementalStays);
      }
    };

    loadStayData();
  }, [packageStayData, packageid, stayMasters]);

  const handleAddStay = (categoryId) => {
    notifyUserEdit();
    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: [
        ...prev[categoryId],
        {
          id: null,
          fromServer: false,
          destination: null,
          duration: "",
          hotels: [
            {
              // Default hotel
              hotel_name: "",
              location: "",
              website: "",
              image_url: null,
              type: "new",
            },
          ],
        },
      ],
    }));
  };

  const handleRemoveStay = async (categoryId, stayIndex) => {
    notifyUserEdit();
    const stay = incrementalStays[categoryId]?.[stayIndex];
    if (!stay) return;

    const previousIncrementalStays = incrementalStays;
    const previousQueryData = queryClient.getQueryData([
      "packageStay",
      packageid,
    ]);

    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].filter((_, index) => index !== stayIndex),
    }));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`${categoryId}_${stayIndex}`];
      return newErrors;
    });

    if (!stay.fromServer || !stay.id) {
      return;
    }

    loadStayDataIdRef.current += 1;

    queryClient.setQueryData(["packageStay", packageid], (old) => {
      if (!old?.data) return old;
      return {
        ...old,
        data: old.data.filter((existingStay) => existingStay.id !== stay.id),
      };
    });

    try {
      await deleteMutation.mutateAsync(stay.id);
      await queryClient.invalidateQueries(["packageStay", packageid]);
    } catch (error) {
      setIncrementalStays(previousIncrementalStays);
      if (previousQueryData) {
        queryClient.setQueryData(["packageStay", packageid], previousQueryData);
      }
      console.error("Failed to delete stay:", error);
      setErrors((prev) => ({
        ...prev,
        [`${categoryId}_${stayIndex}`]: {
          general: ["Failed to delete stay from server"],
        },
      }));
      setShowToster({
        message: "Failed to delete stay from server",
        type: "error",
        show: true,
      });
    }
  };

  const handleStayDestinationChange = async (
    categoryId,
    stayIndex,
    newValue,
  ) => {
    notifyUserEdit();
    if (!newValue) {
      setIncrementalStays((prev) => ({
        ...prev,
        [categoryId]: prev[categoryId].map((stay, index) =>
          index === stayIndex
            ? {
                ...stay,
                destination: null,
                hotels: [
                  {
                    hotel_name: "",
                    location: "",
                    website: "",
                    image_url: null,
                    type: "new",
                  },
                ],
              }
            : stay,
        ),
      }));

      setHotelMasters((prev) => {
        const next = { ...prev };
        delete next[`${categoryId}_${stayIndex}`];
        return next;
      });
      return;
    }

    const isExistingDestination = oldHotelDestinations?.data?.some(
      (dest) =>
        dest.destination === newValue.label || dest.name === newValue.label,
    );

    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((stay, index) =>
        index === stayIndex
          ? {
              ...stay,
              destination: {
                ...newValue,
                type: isExistingDestination ? "existing" : "new",
              },
            }
          : stay,
      ),
    }));

    // Fetch hotels for the new destination
    try {
      const response = await hotelMaster(newValue.label, packageid);
      setHotelMasters((prev) => ({
        ...prev,
        [`${categoryId}_${stayIndex}`]: response.data || [],
      }));
    } catch (error) {
      console.error("Error fetching hotels:", error);
      setErrors({ general: ["Failed to fetch hotels for this destination"] });
    }
  };

  const handleStayDurationChange = (categoryId, stayIndex, value) => {
    notifyUserEdit();
    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((stay, index) =>
        index === stayIndex ? { ...stay, duration: value } : stay,
      ),
    }));
  };

  const handleAddHotelToStay = (categoryId, stayIndex) => {
    notifyUserEdit();
    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((stay, index) =>
        index === stayIndex
          ? {
              ...stay,
              hotels: [
                ...stay.hotels,
                {
                  hotel_name: "",
                  location: "",
                  website: "",
                  image_url: null,
                  type: "new",
                },
              ],
            }
          : stay,
      ),
    }));
  };

  const handleDeleteHotelFromStay = async (
    categoryId,
    stayIndex,
    hotelIndex,
  ) => {
    notifyUserEdit();
    const hotel = incrementalStays[categoryId][stayIndex].hotels[hotelIndex];

    try {
      // If it's an existing hotel with a package_stay_hotel_id, delete it from the server
      if (hotel.package_stay_hotel_id && hotel.type === "existing") {
        await deleteHotelMutation.mutateAsync(hotel.package_stay_hotel_id);
        setShowToster({
          message: "Hotel deleted successfully",
          type: "success",
          show: true,
        });
      }

      // Update the local state by removing the hotel
      setIncrementalStays((prev) => ({
        ...prev,
        [categoryId]: prev[categoryId].map((stay, index) =>
          index === stayIndex
            ? {
                ...stay,
                hotels: stay.hotels.filter((_, i) => i !== hotelIndex),
              }
            : stay,
        ),
      }));

      // Clear any errors for this hotel
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors[`${categoryId}_${stayIndex}`]?.hotels?.[hotelIndex]) {
          const updatedHotelErrors = [
            ...(newErrors[`${categoryId}_${stayIndex}`].hotels || []),
          ];
          updatedHotelErrors[hotelIndex] = null;
          newErrors[`${categoryId}_${stayIndex}`] = {
            ...newErrors[`${categoryId}_${stayIndex}`],
            hotels: updatedHotelErrors,
          };
        }
        return newErrors;
      });
    } catch (error) {
      console.error("Failed to delete hotel:", error);
      setErrors((prev) => ({
        ...prev,
        [`${categoryId}_${stayIndex}`]: {
          ...prev[`${categoryId}_${stayIndex}`],
          general: ["Failed to delete hotel from server"],
        },
      }));
      setShowToster({
        message: "Failed to delete hotel from server",
        type: "error",
        show: true,
      });
    }
  };

  const handleHotelChange = (
    categoryId,
    stayIndex,
    hotelIndex,
    field,
    value,
  ) => {
    notifyUserEdit();
    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((stay, index) =>
        index === stayIndex
          ? {
              ...stay,
              hotels: stay.hotels.map((hotel, i) =>
                i === hotelIndex ? { ...hotel, [field]: value } : hotel,
              ),
            }
          : stay,
      ),
    }));
  };

  const handleImageUpload = (categoryId, stayIndex, hotelIndex, file) => {
    if (!file) return;

    notifyUserEdit();

    const imageUrl = URL.createObjectURL(file);
    setIncrementalStays((prev) => ({
      ...prev,
      [categoryId]: prev[categoryId].map((stay, index) =>
        index === stayIndex
          ? {
              ...stay,
              hotels: stay.hotels.map((hotel, i) =>
                i === hotelIndex
                  ? { ...hotel, image_url: imageUrl, image: file }
                  : hotel,
              ),
            }
          : stay,
      ),
    }));
  };

  const saveStayAt = async (categoryId, stayIndex, { silent = false } = {}) => {
    const stay = incrementalStays[categoryId]?.[stayIndex];
    if (!stay) {
      return { success: false, message: "Stay not found" };
    }

    const stayErrors = {};

    if (!stay.destination?.label?.trim()) {
      stayErrors.destination = ["Destination is required"];
    }

    if (!stay.duration) {
      stayErrors.duration = ["Duration is required"];
    }

    const hotelErrors = stay.hotels.map((hotel) => {
      const errors = {};

      if (!hotel.hotel_name?.trim()) {
        errors.hotel_name = ["Hotel name is required"];
      }

      if (!hotel.location?.trim()) {
        errors.location = ["Location is required"];
      }

      if (!hotel.image_url && !hotel.image) {
        errors.image = ["Hotel image is required"];
      }

      return Object.keys(errors).length > 0 ? errors : null;
    });

    if (hotelErrors.some((error) => error !== null)) {
      stayErrors.hotels = hotelErrors;
    }

    if (Object.keys(stayErrors).length > 0) {
      setErrors((prev) => ({
        ...prev,
        [`${categoryId}_${stayIndex}`]: stayErrors,
      }));
      return {
        success: false,
        message: `Please complete all required fields for Stay ${stayIndex + 1}`,
      };
    }

    const isUpdate = Boolean(stay.fromServer && stay.id);
    const updateId = isUpdate ? stay.id : null;

    const formData = new FormData();
    formData.append("current_stage", "4");
    formData.append("destination", stay.destination?.label || "");
    formData.append("destination_type", stay.destination?.type || "new");
    formData.append("duration", stay.duration || "");
    formData.append("package_id", packageid);
    formData.append("is_draft", 0);
    formData.append("stay_category_id", categoryId);

    const hotelsArray = stay.hotels.map((hotel) => ({
      ...(hotel.id ? { id: hotel.id } : {}),
      type: hotel.type || "new",
      hotel_name: hotel.hotel_name || "",
      location: hotel.location || "",
      website: hotel.website || "",
    }));

    formData.append("hotels", JSON.stringify(hotelsArray));

    stay.hotels.forEach((hotel, index) => {
      if (hotel.image) {
        formData.append(`hotel_images[${index}]`, hotel.image);
      }
    });

    try {
      if (isUpdate && updateId) {
        await updateMutation.mutateAsync({
          formData,
          stayId: updateId,
        });
        if (!silent) {
          setShowToster({
            message: "Stay updated successfully",
            type: "success",
            show: true,
          });
        }
      } else {
        const response = await saveMutation.mutateAsync(formData);
        const createdStay = response?.data;
        if (createdStay?.id) {
          setIncrementalStays((prev) => ({
            ...prev,
            [categoryId]: prev[categoryId].map((currentStay, index) =>
              index === stayIndex
                ? {
                    ...currentStay,
                    id: createdStay.id,
                    fromServer: true,
                  }
                : currentStay
            ),
          }));
        }
        if (!silent) {
          setShowToster({
            message: "Stay saved successfully",
            type: "success",
            show: true,
          });
        }
      }

      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`${categoryId}_${stayIndex}`];
        return newErrors;
      });

      await queryClient.invalidateQueries(["packageStay", packageid]);
      return { success: true };
    } catch (error) {
      console.error("Operation failed:", error);

      if (error.response?.status === 422) {
        try {
          const errorMessage = error.response.data.message;
          const parsedErrors = JSON.parse(errorMessage);
          setErrors((prev) => ({
            ...prev,
            [`${categoryId}_${stayIndex}`]: parsedErrors,
          }));
          if (!silent) {
            setShowToster({
              message: "Please check all required fields",
              type: "error",
              show: true,
            });
          }
        } catch (e) {
          setErrors((prev) => ({
            ...prev,
            [`${categoryId}_${stayIndex}`]: {
              general: ["Validation failed. Please check your input."],
            },
          }));
          if (!silent) {
            setShowToster({
              message: "Validation failed. Please check your input.",
              type: "error",
              show: true,
            });
          }
        }
      } else {
        setErrors((prev) => ({
          ...prev,
          [`${categoryId}_${stayIndex}`]: {
            general: [error.message || "Failed to save stay"],
          },
        }));
        if (!silent) {
          setShowToster({
            message: error.message || "Failed to save stay",
            type: "error",
            show: true,
          });
        }
      }

      return {
        success: false,
        message: error.message || "Failed to save stay",
      };
    }
  };

  const saveFromFooter = async () => {
    const enabledEntries = Object.entries(stayStates).filter(
      ([, state]) => state.enabled,
    );

    if (!enabledEntries.length) {
      return {
        success: false,
        message: "Please enable at least one stay category",
      };
    }

    for (const [categoryId] of enabledEntries) {
      const stays = incrementalStays[categoryId] || [];
      if (!stays.length) {
        return {
          success: false,
          message: "Please add at least one stay for each enabled category",
        };
      }

      for (let stayIndex = 0; stayIndex < stays.length; stayIndex += 1) {
        const result = await saveStayAt(categoryId, stayIndex, {
          silent: true,
        });
        if (!result.success) {
          return result;
        }
      }
    }

    return {
      success: true,
      stay_category_ids: enabledEntries.map(([categoryId]) =>
        Number(categoryId),
      ),
    };
  };

  useImperativeHandle(ref, () => ({
    saveFromFooter,
  }));

  const renderStaySection = (stay, stayIndex) => {
    const stayState = stayStates[stay.id] || { enabled: false, hotels: [] };
    const categoryStays = incrementalStays[stay.id] || [];

    return (
      <div key={stay.id} className={`mb-6 ${pbSectionCard}`}>
        {/* Stay Category Header */}
        <div className="flex items-center justify-between rounded-t-xl border-b border-gray-100 bg-slate-50/50 px-4 py-4 sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm">
              <i className="fi fi-rr-bed text-[13px]"></i>
            </div>
            <div>
              <h3 className={pbSectionTitle}>{stay.title}</h3>
              <p className={pbSectionSubtitle}>
                Configure accommodation details
              </p>
            </div>
          </div>
          <label className="relative inline-flex cursor-pointer items-center">
            <input
              type="checkbox"
              checked={stayState.enabled}
              onChange={(e) => {
                const isEnabled = e.target.checked;
                notifyUserEdit();
                setStayStates((prev) => ({
                  ...prev,
                  [stay.id]: {
                    enabled: isEnabled,
                    hotels: [],
                  },
                }));
                if (isEnabled && categoryStays.length === 0) {
                  setIncrementalStays((prev) => ({
                    ...prev,
                    [stay.id]: [
                      {
                        id: null,
                        fromServer: false,
                        destination: null,
                        duration: "",
                        hotels: [
                          {
                            hotel_name: "",
                            location: "",
                            website: "",
                            image_url: null,
                            type: "new",
                          },
                        ],
                      },
                    ],
                  }));
                }
              }}
              className="sr-only peer"
            />
            <div className="peer relative h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-gray-900 peer-checked:after:translate-x-full peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-900/10"></div>
          </label>
        </div>

        {/* Stay Category Content */}
        {stayState.enabled && (
          <div className="p-4 sm:p-5">
            <div className="space-y-5">
              {categoryStays.map((incrementalStay, stayIndex) => {
                const stayErrors = errors[`${stay.id}_${stayIndex}`] || {};
                const stayDestination =
                  incrementalStay.destination?.label?.trim() || "";

                return (
                  <div key={stayIndex} className={pbInnerSection}>
                    <div className="mb-5 flex items-center justify-between border-b border-gray-100 pb-4">
                      <div className="flex items-center gap-3">
                        <div className={pbStayIndexBadge}>{stayIndex + 1}</div>
                        <div>
                          <h4 className={pbSectionTitle}>
                            Stay {stayIndex + 1}
                          </h4>
                          <p className={pbSectionSubtitle}>
                            {stayDestination ||
                              "Add destination, duration, and hotels"}
                          </p>
                        </div>
                      </div>
                      {categoryStays.length > 1 && (
                        <button
                          onClick={() => handleRemoveStay(stay.id, stayIndex)}
                          className={pbDeleteBtn}
                          title="Remove this stay"
                        >
                          <i className="fi fi-rr-trash text-xs"></i>
                        </button>
                      )}
                    </div>

                    {/* Error Message */}
                    {stayErrors.general && (
                      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3">
                        <div className="flex items-center gap-2">
                          <i className="fi fi-rs-exclamation text-red-500 text-sm"></i>
                          <p className="text-red-700 text-xs">
                            {stayErrors.general[0]}
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="mb-5 grid grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className={pbFieldLabel}>
                          Destination <span className="text-red-500">*</span>
                        </label>
                        <CreatableSelect
                          value={incrementalStay.destination}
                          onChange={(newValue) =>
                            handleStayDestinationChange(
                              stay.id,
                              stayIndex,
                              newValue,
                            )
                          }
                          options={
                            oldHotelDestinations?.data?.map((dest) => ({
                              value: dest.id,
                              label: dest.destination || dest.name,
                            })) || []
                          }
                          className={`basic-single ${stayErrors.destination ? "border-red-500" : ""}`}
                          classNamePrefix="select"
                          isClearable
                          placeholder="Select or create destination..."
                          styles={{
                            control: (base) => ({
                              ...base,
                              minHeight: "29px",
                              height: "29px",
                              borderColor: stayErrors.destination
                                ? "#ef4444"
                                : base.borderColor,
                              borderRadius: "0.5rem",
                              boxShadow: base.isFocused
                                ? "0 0 0 1px #2563eb"
                                : "none",
                              "&:hover": {
                                borderColor: stayErrors.destination
                                  ? "#ef4444"
                                  : base.borderColor,
                              },
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              padding: "0 6px",
                              fontSize: "13px",
                              height: "27px",
                              display: "flex",
                              alignItems: "center",
                            }),
                            input: (base) => ({
                              ...base,
                              margin: 0,
                              padding: "0",
                              fontSize: "13px",
                              height: "27px",
                            }),
                            placeholder: (base) => ({
                              ...base,
                              fontSize: "13px",
                              fontWeight: "normal",
                            }),
                            singleValue: (base) => ({
                              ...base,
                              fontSize: "13px",
                              fontWeight: "500",
                            }),
                            indicatorsContainer: (base) => ({
                              ...base,
                              height: "27px",
                            }),
                            indicatorSeparator: () => ({
                              display: "none",
                            }),
                            dropdownIndicator: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "27px",
                            }),
                            clearIndicator: (base) => ({
                              ...base,
                              padding: "0 6px",
                              height: "27px",
                            }),
                            menu: (base) => ({
                              ...base,
                              fontSize: "13px",
                            }),
                            option: (base) => ({
                              ...base,
                              fontSize: "13px",
                              padding: "4px 8px",
                            }),
                          }}
                        />
                        {stayErrors.destination && (
                          <p className="text-red-500 text-xs mt-1">
                            {stayErrors.destination[0]}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className={pbFieldLabel}>
                          Duration <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={incrementalStay.duration}
                          onChange={(e) =>
                            handleStayDurationChange(
                              stay.id,
                              stayIndex,
                              e.target.value,
                            )
                          }
                          className={`${pbFieldInput} ${
                            stayErrors.duration ? "border-red-500" : ""
                          }`}
                          placeholder="Enter duration in days"
                          min="1"
                        />
                        {stayErrors.duration && (
                          <p className="text-red-500 text-xs mt-1">
                            {stayErrors.duration[0]}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className={pbHotelPanel}>
                      <div className="mb-4 flex items-start justify-between gap-3">
                        <div>
                          <h5 className={pbSectionTitle}>Hotel options</h5>
                          <p className={pbSectionSubtitle}>
                            Customers see these on the website as{" "}
                            <span className="font-medium text-gray-700">
                              Hotel A or Hotel B
                            </span>
                          </p>
                        </div>
                        <span className={pbHotelCountBadge}>
                          {incrementalStay.hotels.length}
                        </span>
                      </div>

                      <div className="space-y-3">
                        {incrementalStay.hotels.map((hotel, hotelIndex) => (
                          <div key={hotelIndex} className={pbHotelCard}>
                            <div className="mb-3 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={pbIndexBadgeSm}>
                                  {hotelIndex + 1}
                                </div>
                                <span className="text-sm font-medium text-gray-800">
                                  Hotel {hotelIndex + 1}
                                </span>
                              </div>
                              <button
                                onClick={async () => {
                                  await handleDeleteHotelFromStay(
                                    stay.id,
                                    stayIndex,
                                    hotelIndex,
                                  );
                                }}
                                disabled={deleteHotelMutation.isPending}
                                className={`${pbDeleteBtn} ${pbDeleteBtnDisabled}`}
                                title="Remove this hotel"
                              >
                                {deleteHotelMutation.isPending ? (
                                  <i className="fi fi-rs-spinner animate-spin text-xs"></i>
                                ) : (
                                  <i className="fi fi-rr-trash text-xs"></i>
                                )}
                              </button>
                            </div>
                            {renderHotelInputs(stay.id, stayIndex, hotelIndex)}
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={() => handleAddHotelToStay(stay.id, stayIndex)}
                        className={pbAddBtnMt}
                      >
                        <i className="fi fi-rr-plus text-xs"></i>
                        Add another Hotel Option
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Add Another Stay Button */}
            <button
              onClick={() => handleAddStay(stay.id)}
              className={`mt-4 ${pbAddBtnFull}`}
            >
              <i className="fi fi-rr-plus text-xs"></i>
              Add Another Stay
            </button>
          </div>
        )}
      </div>
    );
  };

  // Update parent component with stay data
  useEffect(() => {
    if (setStayData) {
      const enabledCategoryIds = Object.entries(stayStates)
        .filter(([, state]) => state.enabled)
        .map(([categoryId]) => Number(categoryId));

      const allStays = enabledCategoryIds.flatMap((categoryId) => {
        const stays = incrementalStays[categoryId] || [];
        return stays.map((stay) => ({
          ...(stay.fromServer && stay.id ? { id: stay.id } : {}),
          stay_category_id: categoryId,
          destination: stay.destination?.label || "",
          destination_type: stay.destination?.type || "new",
          duration: parseInt(stay.duration) || 0,
          package_id: packageid,
          hotels: stay.hotels.map((hotel) => ({
            hotel_name: hotel.hotel_name,
            location: hotel.location,
            website: hotel.website,
            image_url: hotel.image_url,
            type: hotel.type || "new",
          })),
        }));
      });

      const hasValidStayInEachCategory = enabledCategoryIds.every(
        (categoryId) => {
          const stays = incrementalStays[categoryId] || [];
          return stays.some(
            (stay) =>
              stay.destination?.label &&
              stay.duration &&
              stay.hotels.length > 0 &&
              stay.hotels.every((hotel) => hotel.hotel_name),
          );
        },
      );

      const hasSavedStays = enabledCategoryIds.some((categoryId) =>
        (incrementalStays[categoryId] || []).some((stay) => stay.fromServer),
      );

      setStayData({
        data: allStays,
        current_stage: 4,
        hasValidStays: hasSavedStays || hasValidStayInEachCategory,
        enabledCategoriesCount: enabledCategoryIds.length,
      });
    }
  }, [setStayData, stayStates, incrementalStays, packageid]);

  const renderHotelInputs = (categoryId, stayIndex, hotelIndex) => {
    const hotel = incrementalStays[categoryId][stayIndex].hotels[hotelIndex];
    const stayErrors = errors[`${categoryId}_${stayIndex}`] || {};
    const hotelErrors = stayErrors.hotels?.[hotelIndex] || {};

    const hotelOptions =
      hotelMasters[`${categoryId}_${stayIndex}`]?.map((hotelMaster) => ({
        value: hotelMaster.id,
        label: hotelMaster.hotel_name,
        data: hotelMaster,
      })) || [];

    // Find the matching option for the current hotel
    const selectedOption = hotel.id
      ? {
          value: hotel.id,
          label: hotel.hotel_name,
          data:
            hotelMasters[`${categoryId}_${stayIndex}`]?.find(
              (h) => h.id === hotel.id,
            ) || hotel,
        }
      : hotel.hotel_name
        ? { value: hotel.hotel_name, label: hotel.hotel_name }
        : null;

    return (
      <div className="space-y-3">
        {/* Hotel Name Selection */}
        <div>
          <label className={pbFieldLabel}>
            Hotel Name <span className="text-red-500">*</span>
          </label>
          <CreatableSelect
            value={selectedOption}
            onChange={(option) => {
              notifyUserEdit();
              if (option) {
                const selectedHotel = option.data || null;

                if (selectedHotel) {
                  // For existing hotel
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "id",
                    selectedHotel.id,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "hotel_name",
                    selectedHotel.hotel_name,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "type",
                    "existing",
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "image_url",
                    selectedHotel.image_url,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "location",
                    selectedHotel.location,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "website",
                    selectedHotel.website,
                  );
                } else if (option.__isNew__) {
                  // For new hotel
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "id",
                    null,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "hotel_name",
                    option.value,
                  );
                  handleHotelChange(
                    categoryId,
                    stayIndex,
                    hotelIndex,
                    "type",
                    "new",
                  );
                  // Keep existing values for other fields if they exist
                  if (!hotel.image_url)
                    handleHotelChange(
                      categoryId,
                      stayIndex,
                      hotelIndex,
                      "image_url",
                      null,
                    );
                  if (!hotel.location)
                    handleHotelChange(
                      categoryId,
                      stayIndex,
                      hotelIndex,
                      "location",
                      "",
                    );
                  if (!hotel.website)
                    handleHotelChange(
                      categoryId,
                      stayIndex,
                      hotelIndex,
                      "website",
                      "",
                    );
                }
              } else {
                // Clear all fields when selection is cleared
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "id",
                  null,
                );
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "hotel_name",
                  "",
                );
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "type",
                  "new",
                );
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "image_url",
                  null,
                );
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "location",
                  "",
                );
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "website",
                  "",
                );
              }
            }}
            options={hotelOptions}
            className={`w-full ${hotelErrors.hotel_name ? "border-red-500" : ""}`}
            classNamePrefix="select"
            placeholder="Select or create hotel..."
            isClearable
            isSearchable
            formatCreateLabel={(inputValue) => `Add new hotel "${inputValue}"`}
            styles={{
              control: (base) => ({
                ...base,
                minHeight: "29px",
                height: "29px",
                borderColor: hotelErrors.hotel_name
                  ? "#ef4444"
                  : base.borderColor,
                borderRadius: "0.5rem",
                boxShadow: base.isFocused ? "0 0 0 1px #2563eb" : "none",
                "&:hover": {
                  borderColor: hotelErrors.hotel_name
                    ? "#ef4444"
                    : base.borderColor,
                },
              }),
              valueContainer: (base) => ({
                ...base,
                padding: "0 6px",
                fontSize: "13px",
                height: "27px",
                display: "flex",
                alignItems: "center",
              }),
              input: (base) => ({
                ...base,
                margin: 0,
                padding: "0",
                fontSize: "13px",
                height: "27px",
              }),
              placeholder: (base) => ({
                ...base,
                fontSize: "13px",
                fontWeight: "normal",
              }),
              singleValue: (base) => ({
                ...base,
                fontSize: "13px",
                fontWeight: "500",
              }),
              indicatorsContainer: (base) => ({
                ...base,
                height: "27px",
              }),
              indicatorSeparator: () => ({
                display: "none",
              }),
              dropdownIndicator: (base) => ({
                ...base,
                padding: "0 6px",
                height: "27px",
              }),
              clearIndicator: (base) => ({
                ...base,
                padding: "0 6px",
                height: "27px",
              }),
              menu: (base) => ({
                ...base,
                fontSize: "13px",
              }),
              option: (base) => ({
                ...base,
                fontSize: "13px",
                padding: "4px 8px",
              }),
            }}
          />
          {hotelErrors.hotel_name && (
            <p className="text-red-500 text-xs mt-1">
              {hotelErrors.hotel_name[0]}
            </p>
          )}
        </div>

        {/* Hotel Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={pbFieldLabel}>
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={hotel.location || ""}
              onChange={(e) =>
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "location",
                  e.target.value,
                )
              }
              disabled={hotel.type === "existing"}
              className={`w-full px-3 py-1 border ${
                hotelErrors.location ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary text-sm font-medium ${
                hotel.type === "existing"
                  ? "bg-gray-100 cursor-not-allowed text-gray-600"
                  : "text-gray-800"
              } placeholder:font-normal`}
              placeholder="Enter location"
            />
            {hotelErrors.location && (
              <p className="text-red-500 text-xs mt-1">
                {hotelErrors.location[0]}
              </p>
            )}
          </div>
          <div>
            <label className={pbFieldLabel}>Website</label>
            <input
              type="url"
              value={hotel.website || ""}
              onChange={(e) =>
                handleHotelChange(
                  categoryId,
                  stayIndex,
                  hotelIndex,
                  "website",
                  e.target.value,
                )
              }
              disabled={hotel.type === "existing"}
              className={`w-full px-3 py-1 border ${
                hotelErrors.website ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary text-sm font-medium ${
                hotel.type === "existing"
                  ? "bg-gray-100 cursor-not-allowed text-gray-600"
                  : "text-gray-800"
              } placeholder:font-normal`}
              placeholder="Enter website URL"
            />
            {hotelErrors.website && (
              <p className="text-red-500 text-xs mt-1">
                {hotelErrors.website[0]}
              </p>
            )}
          </div>
        </div>

        {/* Hotel Image */}
        <div>
          <label className={pbFieldLabel}>
            Hotel Image <span className="text-red-500">*</span>
          </label>
          <ImageInput
            showLabel={false}
            onChange={(image) =>
              handleImageUpload(categoryId, stayIndex, hotelIndex, image)
            }
            label="Upload Image"
            required={true}
            error={hotelErrors.image}
            disabled={hotel.type === "existing"}
          />
        </div>

        {/* Hotel Image Preview */}
        {hotel.image_url && (
          <div className="rounded-lg border border-gray-200/80 bg-slate-50/50 p-3">
            <FilePreview
              file={hotel.image_url}
              label="Hotel Image"
              onDelete={
                hotel.type !== "existing"
                  ? () =>
                      handleHotelChange(
                        categoryId,
                        stayIndex,
                        hotelIndex,
                        "image_url",
                        null,
                      )
                  : undefined
              }
              isImage={true}
              showDelete={hotel.type === "existing"}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-0 w-full">
      <div className="space-y-5">
        {stayMasters?.data?.map((stay, stayIndex) =>
          renderStaySection(stay, stayIndex),
        )}
      </div>

      {/* Toast */}
      {showToster && (
        <Toster
          message={showToster.message}
          type={showToster.type}
          onClose={() => setShowToster(false)}
        />
      )}
    </div>
  );
});

StayInfo.displayName = "StayInfo";

export default StayInfo;
